<template>
  <section class="bonita__form">
    <h1>{{ $t('forms.researcher.mainTitle') }}</h1>
    <h2>{{ $t('forms.researcher.mainSbuTitle') }}</h2>

    <dates-of-stay :valid.sync="datesOfStay.valid"
                   :arrival-date.sync="datesOfStay.arrivalDate"
                   :departure-date.sync="datesOfStay.departureDate"
    />

    <contact-details :valid.sync="contactDetails.valid"
                     :address.sync="contactDetails.address"
                     :birthday.sync="contactDetails.birthday"
                     :birthLocation.sync="contactDetails.birthLocation"
                     :city.sync="contactDetails.city"
                     :country.sync="contactDetails.country"
                     :firstName.sync="contactDetails.firstName"
                     :lastName.sync="contactDetails.lastName"
                     :mail.sync="contactDetails.mail"
                     :nationality.sync="contactDetails.nationality"
                     :phone.sync="contactDetails.phone"
                     :sex.sync="contactDetails.sex"
                     :status.sync="contactDetails.status"
                     :status-items.sync="contactDetails.statusItems"
                     :title-other.sync="contactDetails.titleOther"
                     :zipCode.sync="contactDetails.zipCode"
    />

    <registration :valid.sync="registration.valid"
                  :registration-address.sync="registration.registrationAddress"
                  :mail.sync="registration.registrationEmail"
                  :registration-entity.sync="registration.registrationEntity"
                  :registration-manager.sync="registration.registrationManager"
                  :registration-phone.sync="registration.registrationPhone"
                  :registration-referent.sync="registration.registrationReferent"
    />

    <accommodation :valid.sync="accommodation.valid"
                   :arconati.sync="accommodation.arconati"
                   :arconati-choice.sync="accommodation.arconatiChoice"
                   :mui.sync="accommodation.mui"
                   :mui-choice1.sync="accommodation.muiChoice1"
                   :mui-choice2.sync="accommodation.muiChoice2"
                   :mui-choice3.sync="accommodation.muiChoice3"
    />

    <more-information :complete-choice="true"
                      :display-children="true"
                      :information-message.sync="moreInformation.informationMessage"
                      :information-number.sync="moreInformation.informationNumber"
                      :information-number-adults.sync="moreInformation.informationNumberAdults"
                      :information-number-children.sync="moreInformation.informationNumberChildren"
                      :information-payment.sync="moreInformation.informationPayment"
    />

    <authorization :valid.sync="authorization.valid"
                   :content="$t('forms.researcher.authorization.content')"
                   :mui.sync="accommodation.mui"
                   :mui-authorization.sync="authorization.authorizationChecker"
    />

    <nota-bene :content="$t('forms.researcher.notaBene.content')"/>

    <v-btn @click="onSubmit"
           color="blue-grey"
           :disabled="!valid"
    >{{ $t('forms.researcher.button') }}
    </v-btn>
  </section>
</template>

<script>
import { ResearcherData } from '@/mixins/dataBaseMixin'

export default {
  name: 'Researcher',

  components: {
    NotaBene: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/NotaBene'),
    Authorization: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/Authorization'),
    DatesOfStay: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/DatesOfStay'),
    ContactDetails: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/ContactDetails'),
    Registration: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/Registration'),
    Accommodation: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/Accommodation'),
    MoreInformation: () => import(/* webpackChunkName: "researcher" */ '@/components/Parts/MoreInformation'),
  },

  data () {
    return {
      datesOfStay: {
        valid: false,
        arrivalDate: null,
        departureDate: null,
      },
      contactDetails: {
        valid: false,
        address: '',
        birthday: '',
        birthLocation: '',
        city: '',
        country: '',
        firstName: '',
        lastName: '',
        mail: '',
        nationality: '',
        phone: '',
        sex: '',
        status: '',
        statusItems: [
          {
            text: this.$t('contactDetails.status.items.visitingScholar'),
            value: 'visitingScholar',
          },
          {
            text: this.$t('contactDetails.status.items.postDoc'),
            value: 'postDoc',
          },
          {
            text: this.$t('contactDetails.status.items.guestLecturer'),
            value: 'guestLecturer',
          },
          {
            text: this.$t('contactDetails.status.items.other'),
            value: 'other',
          },
        ],
        titleOther: '',
        zipCode: '',
      },
      registration: {
        valid: false,
        registrationAddress: '',
        registrationEmail: '',
        registrationEntity: '',
        registrationManager: '',
        registrationPhone: '',
        registrationReferent: '',
      },
      accommodation: {
        valid: false,
        arconati: false,
        arconatiChoice: '',
        mui: false,
        muiChoice1: '',
        muiChoice2: '',
        muiChoice3: '',
      },
      moreInformation: {
        informationMessage: '',
        informationNumber: '',
        informationNumberAdults: '',
        informationNumberChildren: '',
        informationPayment: '',
      },
      authorization: {
        valid: false,
        authorizationChecker: false,
      },
    }
  },

  computed: {
    valid () {
      return this.datesOfStay.valid &&
        this.contactDetails.valid &&
        this.registration.valid &&
        this.accommodation.valid &&
        this.authorization.valid &&
        !this.$store.getters['submission']
    },
  },

  methods: {
    onSubmit () {
      let dataForm = {
        arrivalDate: this.datesOfStay.arrivalDate,
        departureDate: this.datesOfStay.departureDate,
        sex: this.contactDetails.sex,
        lastName: this.contactDetails.lastName,
        firstName: this.contactDetails.firstName,
        nationality: this.contactDetails.nationality,
        birthday: this.contactDetails.birthday,
        birthLocation: this.contactDetails.birthLocation,
        address: this.contactDetails.address,
        zipCode: this.contactDetails.zipCode,
        city: this.contactDetails.city,
        country: this.contactDetails.country,
        phone: this.contactDetails.phone,
        mail: this.contactDetails.mail,
        status: this.contactDetails.status,
        registrationAddress: this.registration.registrationAddress,
        registrationEmail: this.registration.registrationEmail,
        registrationEntity: this.registration.registrationEntity,
        registrationManager: this.registration.registrationManager,
        registrationPhone: this.registration.registrationPhone,
        registrationReferent: this.registration.registrationReferent,
        informationPayment: this.moreInformation.informationPayment,
        informationMessage: this.moreInformation.informationMessage,
        informationNumber: this.moreInformation.informationNumber,
      }

      if (this.contactDetails.status === 'other') {
        dataForm.titleOther = this.contactDetails.titleOther
      }

      if (this.accommodation.arconati) {
        dataForm.arconatiAccomodation4 = true
        dataForm.arconatiChoice = this.accommodation.arconatiChoice
      }

      if (this.accommodation.mui) {
        dataForm.muiChoice1 = this.accommodation.muiChoice1
        dataForm.muiChoice2 = this.accommodation.muiChoice2
        dataForm.muiChoice3 = this.accommodation.muiChoice3
        dataForm.muiAuthorizationChecker = this.authorization.muiAuthorizationChecker
      }

      if (this.moreInformation.informationNumber === 'plusOne') {
        dataForm.informationNumberAdults = this.moreInformation.informationNumberAdults
        dataForm.informationNumberChildren = this.moreInformation.informationNumberChildren
      }

      dataForm.population = 'researcher'
      dataForm.lang = this.$t('lang')
      this.$store.dispatch('postFormValues', dataForm)
          .then(() => {
            this.$store.dispatch('postFormValuesValidation', dataForm)
            this.resetData()
          })
          .catch(() => {
            this.$store.dispatch('postFormValuesError')
          })
    },
    resetData () {
      Object.assign(this.$data, ResearcherData())
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "../../assets/style";
</style>
